import { useCallback, useEffect, useState, useMemo } from "react";
import { useFetch } from "../../../utils/hooks/useFetch";
import {
  getHolidayListServiceConfig,
  saveHolidayServiceConfig,
  deleteHolidayServiceConfig,
} from "../../../utils/services/Holidays";
import { translate } from "../../../utils/translations/translate";
import { useToastInfo } from "../../../utils/hooks/useToastInfo";
import {
  GET_YEAR_FILTER_OPTIONS,
  replaceErrorText,
} from "../../../utils/helpers";
import {
  deleteProjectTaskServiceConfig,
  getProjectTaskServiceConfig,
  saveProjectTaskServiceConfig,
} from "../../../utils/services/projects";
export const useSettings = () => {
  const { isLoading: holidaysLoading, performAction: executeHolidayService } =
    useFetch();
  const {
    isLoading: projectTasksLoading,
    performAction: executeProjectTaskService,
  } = useFetch();
  const { toastInfo: holidayToast, setToastInfo: setHolidayToast } =
    useToastInfo();
  const { toastInfo: projectTasksToast, setToastInfo: setProjectTasksToast } =
    useToastInfo();
  const [holidaysList, setHolidaysList] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const YEAR_FILTER_OPTIONS = useMemo(() => {
    return GET_YEAR_FILTER_OPTIONS();
  }, []);
  const [selectedYear, setSelectedYear] = useState(
    YEAR_FILTER_OPTIONS[0].value
  );
  const handleYearChange = useCallback((year) => {
    setSelectedYear(year);
  }, []);
  const fetchHolidays = useCallback(
    async (year) => {
      try {
        const { url, options } = getHolidayListServiceConfig(year);
        const holidaysResponse = await executeHolidayService(url, options);
        if (Array.isArray(holidaysResponse?.Result)) {
          const formattedHolidaysList = holidaysResponse.Result.map(
            (holiday) => {
              const {
                HolidayDate,
                HolidayID,
                HolidayName,
                UpdatedOn,
                Description,
              } = holiday;
              return {
                holidayDate: HolidayDate,
                holidayName: HolidayName,
                holidayDescription: Description,
                holidayId: HolidayID,
                holidayModifiedOn: UpdatedOn,
              };
            }
          );
          setHolidaysList(formattedHolidaysList);
          return;
        }
        throw new Error("Unable to fetch holidays");
      } catch (e) {
        console.error("Error fetching holidays", e);
        setHolidayToast({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      }
    },
    [executeHolidayService, setHolidayToast]
  );
  const createEditHoliday = useCallback(
    async (formValues) => {
      try {
        const isCreateMode = !formValues.holidayId;
        const { url, options } = saveHolidayServiceConfig(formValues);
        const serverResponse = await executeHolidayService(url, options);
        if (serverResponse?.StatusCode === 200 && serverResponse.HolidayID) {
          if (isCreateMode) {
            const newHolidayRecord = {
              ...formValues,
              holidayModifiedOn: new Date(),
              holidayId: serverResponse.HolidayID,
            };
            setHolidaysList((prev) => {
              const newHolidays = [...prev, newHolidayRecord];
              return newHolidays;
            });
          } else {
            setHolidaysList((prev) => {
              const updatedList = [...prev];
              const holidayIndex = updatedList.findIndex(
                (listItem) => listItem.holidayId === serverResponse.HolidayID
              );
              if (holidayIndex > -1) {
                updatedList[holidayIndex] = {
                  ...updatedList[holidayIndex],
                  ...formValues,
                  holidayModifiedOn: new Date(),
                };
              }
              return updatedList;
            });
          }
          return setHolidayToast({
            message: isCreateMode
              ? translate("labels.holidayCreatedMessage")
              : translate("labels.holidaysEditedMessage"),
            type: "success",
            autoExpire: true,
          });
        }
        throw new Error(`Unable to Procced with action please try again`);
      } catch (e) {
        setHolidayToast({
          message: replaceErrorText(e.message),
          type: "error",
          autoExpire: true,
        });
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [executeHolidayService, setHolidayToast]
  );
  const deleteHoliday = useCallback(
    async (holidayId) => {
      try {
        const { url, options } = deleteHolidayServiceConfig(holidayId);
        const response = await executeHolidayService(url, options);
        if (response?.StatusCode === 200) {
          setHolidayToast({
            message: translate("labels.holidayDeletedMsg"),
            type: "info",
            autoExpire: true,
          });
          setHolidaysList((prev) => {
            const newlist = prev.filter(
              (holiday) => holiday.holidayId !== holidayId
            );
            return newlist;
          });
          return true;
        }
        throw new Error("Error:Deleting the holiday");
      } catch (e) {
        console.error("Error deleting the holiday");
        setHolidayToast({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [executeHolidayService, setHolidayToast]
  );
  const handleHolidayToastClose = useCallback(() => {
    setHolidayToast(null);
  }, [setHolidayToast]);
  const handleProjectToastClose = useCallback(() => {
    setProjectTasksToast(null);
  }, [setProjectTasksToast]);
  const fetchProjectTasks = useCallback(async () => {
    try {
      const { url, options } = getProjectTaskServiceConfig();
      const projectTasksResponse = await executeProjectTaskService(
        url,
        options
      );
      if (Array.isArray(projectTasksResponse?.Result)) {
        const formattedProjectTasks = projectTasksResponse.Result.map(
          (task) => {
            const { ProjectTaskID, ProjectTaskName, CreatedOn, IsActive } =
              task;
            return {
              taskId: ProjectTaskID,
              taskName: ProjectTaskName,
              taskCreatedOn: CreatedOn,
              isActive: IsActive,
            };
          }
        );
        setProjectTasks(formattedProjectTasks);
        return;
      }
      throw new Error("Unable to fetch Project tasks");
    } catch (e) {
      console.error("Error fetching Project Tasks", e);
      setProjectTasksToast({
        message: e.message,
        type: "error",
        autoExpire: true,
      });
    }
  }, [executeProjectTaskService, setProjectTasksToast]);
  const createEditProjectTasks = useCallback(
    async (formValues) => {
      try {
        const isCreateMode = !formValues.taskId;
        const { url, options } = saveProjectTaskServiceConfig(formValues);
        const serverResponse = await executeProjectTaskService(url, options);
        debugger;
        if (
          serverResponse?.StatusCode === 200 &&
          serverResponse.ProjectTaskID
        ) {
          if (isCreateMode) {
            const newTaskRecord = {
              ...formValues,
              taskId: serverResponse.ProjectTaskID,
              taskCreatedOn: new Date(),
            };
            setProjectTasks((prev) => {
              const newTasks = [...prev, newTaskRecord];
              return newTasks;
            });
          } else {
            setProjectTasks((prev) => {
              const updatedList = [...prev];
              const taskIndex = updatedList.findIndex(
                (listItem) => listItem.taskId === serverResponse.ProjectTaskID
              );
              if (taskIndex > -1) {
                updatedList[taskIndex] = {
                  ...updatedList[taskIndex],
                  ...formValues,
                };
              }
              return updatedList;
            });
          }
          return setProjectTasksToast({
            message: isCreateMode
              ? translate("labels.taskCreatedMessage")
              : translate("labels.taskUpdatedMessage"),
            type: "success",
            autoExpire: true,
          });
        }
        throw new Error(`Unable to Procced with action please try again`);
      } catch (e) {
        setProjectTasksToast({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [executeProjectTaskService, setProjectTasksToast]
  );
  const deleteTask = useCallback(
    async (taskId) => {
      try {
        const { url, options } = deleteProjectTaskServiceConfig(taskId);
        const response = await executeProjectTaskService(url, options);
        if (response?.StatusCode === 200) {
          setProjectTasksToast({
            message: translate("labels.taskDeletedMsg"),
            type: "info",
            autoExpire: true,
          });
          setProjectTasks((prev) => {
            const newlist = prev.filter((task) => task.taskId !== taskId);
            return newlist;
          });
          return true;
        }
        throw new Error("Error:Deleting the task");
      } catch (e) {
        console.error("Error deleting the task");
        setProjectTasksToast({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [executeProjectTaskService, setProjectTasksToast]
  );
  useEffect(() => {
    fetchProjectTasks();
  }, [fetchProjectTasks]);
  useEffect(() => {
    fetchHolidays(selectedYear);
  }, [selectedYear, fetchHolidays]);
  return {
    holidaysList,
    holidaysLoading,
    createEditHoliday,
    deleteHoliday,
    projectTasksToast,
    holidayToast,
    projectTasks,
    selectedYear,
    YEAR_FILTER_OPTIONS,
    handleYearChange,
    createEditProjectTasks,
    deleteTask,
    handleHolidayToastClose,
    handleProjectToastClose,
    projectTasksLoading,
  };
};
